import React, { useState, useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { 
  ShieldCheck, MapPin, Clock, Heart, ChevronRight, Star, 
  CheckCircle2, Phone, Mail, ArrowLeft, Dog, PawPrint
} from 'lucide-react';
import { locationPages } from '../data/locations';
import { faqs, services, testimonials } from '../data/mock';
import LeadForm from '../components/LeadForm';

const LocationPage = () => {
  const { slug } = useParams();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const location = locationPages[slug];

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!location) {
    return <Navigate to="/" replace />;
  }

  const handleGetQuote = () => setIsFormOpen(true);

  // Generate JSON-LD schema for this location
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `https://dogboardingbaltimore.com/${location.slug}/#business`,
    "name": `Dog Boarding ${location.name} - Dog Boarding Baltimore`,
    "description": location.metaDescription,
    "url": `https://dogboardingbaltimore.com/${location.slug}`,
    "telephone": "",
    "email": "team@dogboardingbaltimore.com",
    "image": "https://images.pexels.com/photos/2253275/pexels-photo-2253275.jpeg",
    "priceRange": "$$",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": location.name,
      "addressRegion": "MD",
      "postalCode": location.zipCodes[0],
      "addressCountry": "US"
    },
    "areaServed": [
      location.name,
      ...location.nearbyAreas
    ],
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      "opens": "07:00",
      "closes": "20:00"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "50",
      "bestRating": "5"
    }
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://dogboardingbaltimore.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": `Dog Boarding ${location.name}`,
        "item": `https://dogboardingbaltimore.com/${location.slug}`
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>{location.title}</title>
        <meta name="description" content={location.metaDescription} />
        <meta name="keywords" content={`dog boarding ${location.name}, pet boarding ${location.name} Baltimore, dog daycare ${location.name}, dog kennel ${location.name} MD, overnight dog boarding ${location.name}`} />
        <link rel="canonical" href={`https://dogboardingbaltimore.com/${location.slug}`} />
        
        <meta property="og:title" content={location.title} />
        <meta property="og:description" content={location.metaDescription} />
        <meta property="og:url" content={`https://dogboardingbaltimore.com/${location.slug}`} />
        <meta property="og:type" content="website" />
        
        <meta name="twitter:title" content={location.title} />
        <meta name="twitter:description" content={location.metaDescription} />
        
        <script type="application/ld+json">
          {JSON.stringify(localBusinessSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Header */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-stone-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16 md:h-20">
              <a href="/" className="flex items-center gap-2 group">
                <div className="w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center group-hover:bg-amber-600 transition-colors">
                  <Dog className="w-6 h-6 text-white" />
                </div>
                <div className="hidden sm:block">
                  <span className="font-bold text-lg text-stone-800">Dog Boarding</span>
                  <span className="block text-xs text-amber-600 font-medium -mt-1">Baltimore</span>
                </div>
              </a>
              <Button
                onClick={handleGetQuote}
                className="bg-amber-500 hover:bg-amber-600 text-white font-semibold px-6 py-2 rounded-lg"
              >
                Get Free Quote
              </Button>
            </div>
          </div>
        </header>

        {/* Breadcrumb */}
        <div className="pt-20 md:pt-24 bg-stone-50 border-b border-stone-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <nav className="flex items-center gap-2 text-sm">
              <a href="/" className="text-stone-500 hover:text-amber-600 flex items-center gap-1">
                <ArrowLeft className="w-4 h-4" />
                Home
              </a>
              <span className="text-stone-300">/</span>
              <span className="text-stone-800 font-medium">Dog Boarding {location.name}</span>
            </nav>
          </div>
        </div>

        {/* Hero Section */}
        <section className="bg-gradient-to-b from-stone-50 to-white py-12 md:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 bg-amber-100 rounded-full px-4 py-2 mb-6">
                  <MapPin className="w-4 h-4 text-amber-600" />
                  <span className="text-sm font-medium text-amber-700">Serving {location.name}, Baltimore</span>
                </div>
                
                <h1 className="text-4xl md:text-5xl font-bold text-stone-800 leading-tight mb-6">
                  {location.heroHeadline}
                </h1>
                
                <p className="text-lg text-stone-600 mb-8 leading-relaxed">
                  {location.heroSubheadline}
                </p>

                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                  <Button
                    onClick={handleGetQuote}
                    className="bg-amber-500 hover:bg-amber-600 text-white font-semibold px-8 py-6 text-lg rounded-xl shadow-lg"
                  >
                    Get Your Free Quote
                    <ChevronRight className="ml-2 w-5 h-5" />
                  </Button>
                </div>

                <div className="flex flex-wrap gap-4">
                  {[
                    { icon: ShieldCheck, text: 'Background Checked' },
                    { icon: MapPin, text: 'Locally Owned' },
                    { icon: Clock, text: '5+ Years Trusted' }
                  ].map((badge, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <badge.icon className="w-5 h-5 text-amber-600" />
                      <span className="text-sm font-medium text-stone-700">{badge.text}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="relative">
                <img
                  src="https://images.pexels.com/photos/2253275/pexels-photo-2253275.jpeg"
                  alt={`Dog boarding in ${location.name} Baltimore`}
                  className="rounded-2xl shadow-2xl w-full h-[400px] object-cover"
                />
                <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg p-4">
                  <div className="flex items-center gap-2">
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-5 h-5 text-amber-500 fill-amber-500" />
                      ))}
                    </div>
                    <span className="font-semibold text-stone-800">4.9/5</span>
                  </div>
                  <p className="text-sm text-stone-500 mt-1">Trusted by {location.name} families</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16 md:py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <article className="prose prose-lg prose-stone max-w-none">
              <h2 className="text-3xl font-bold text-stone-800 mb-6">
                Dog Boarding in {location.name} – Your Trusted Local Choice
              </h2>
              
              <p className="text-stone-600 leading-relaxed text-lg">
                {location.content.intro}
              </p>

              <h3 className="text-2xl font-bold text-stone-800 mt-10 mb-4">
                Why {location.name} Dog Owners Choose Us
              </h3>
              <p className="text-stone-600 leading-relaxed">
                {location.content.whyUs}
              </p>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 my-8">
                <h4 className="text-xl font-bold text-stone-800 mb-4 flex items-center gap-2">
                  <PawPrint className="w-6 h-6 text-amber-600" />
                  What Sets Us Apart in {location.name}
                </h4>
                <ul className="space-y-3">
                  {[
                    '100% background-checked staff – your pet\'s safety is our priority',
                    'Locally owned & operated – not a corporate chain',
                    'Trusted by ' + location.name + ' neighbors for 5+ years',
                    'Daily photo & video updates while your dog stays with us',
                    'Flexible drop-off and pick-up times for busy schedules',
                    'Personalized attention for every dog\'s unique needs'
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-stone-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <h3 className="text-2xl font-bold text-stone-800 mt-10 mb-4">
                Understanding {location.name} Dogs
              </h3>
              <p className="text-stone-600 leading-relaxed">
                {location.content.localLife}
              </p>

              <h3 className="text-2xl font-bold text-stone-800 mt-10 mb-4">
                Convenient Dog Boarding for {location.name} Residents
              </h3>
              <p className="text-stone-600 leading-relaxed">
                {location.content.convenience}
              </p>
            </article>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-16 bg-stone-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-stone-800 text-center mb-4">
              Our Dog Boarding Services in {location.name}
            </h2>
            <p className="text-lg text-stone-600 text-center max-w-2xl mx-auto mb-12">
              Whether you need overnight boarding, daycare, or extended stays – {location.name} dogs receive the same exceptional care.
            </p>
            
            <div className="grid md:grid-cols-3 gap-8">
              {services.map((service, i) => (
                <div key={i} className="bg-white rounded-xl p-6 shadow-lg border border-stone-100">
                  <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-4">
                    <PawPrint className="w-6 h-6 text-amber-600" />
                  </div>
                  <h3 className="text-xl font-bold text-stone-800 mb-2">{service.title}</h3>
                  <p className="text-stone-600">{service.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonial */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-stone-800 text-center mb-12">
              What {location.name} Dog Owners Say
            </h2>
            <div className="bg-stone-50 rounded-2xl p-8 border border-stone-200">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-6 h-6 text-amber-500 fill-amber-500" />
                ))}
              </div>
              <p className="text-xl text-stone-700 italic mb-6">
                "{testimonials[0].text}"
              </p>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold">{testimonials[0].name.charAt(0)}</span>
                </div>
                <div>
                  <p className="font-semibold text-stone-800">{testimonials[0].name}</p>
                  <p className="text-sm text-stone-500">{location.name} Resident</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Areas We Also Serve */}
        <section className="py-16 bg-stone-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-stone-800 text-center mb-8">
              Also Serving Nearby Areas
            </h2>
            <div className="flex flex-wrap justify-center gap-3">
              {location.nearbyAreas.map((area, i) => (
                <span key={i} className="bg-white px-4 py-2 rounded-full text-stone-600 border border-stone-200">
                  {area}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-stone-800 text-center mb-4">
              Dog Boarding {location.name} – FAQ
            </h2>
            <p className="text-lg text-stone-600 text-center mb-12">
              Common questions from {location.name} dog owners
            </p>
            
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.slice(0, 4).map((faq, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  className="bg-white rounded-xl border border-stone-200 px-6"
                >
                  <AccordionTrigger className="text-left text-stone-800 font-semibold py-5 hover:no-underline">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-stone-600 pb-5">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-amber-500">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready for the Best Dog Boarding in {location.name}?
            </h2>
            <p className="text-lg text-amber-100 mb-8">
              Join {location.name} families who trust us with their furry friends. Get your free quote today!
            </p>
            <Button
              onClick={handleGetQuote}
              className="bg-white text-amber-600 hover:bg-amber-50 font-bold px-10 py-6 text-lg rounded-xl"
            >
              Get Your Free Quote
              <ChevronRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-stone-900 text-stone-300 py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-6">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center">
                  <Dog className="w-6 h-6 text-white" />
                </div>
                <div>
                  <span className="font-bold text-white">Dog Boarding</span>
                  <span className="block text-xs text-amber-500 -mt-1">Baltimore</span>
                </div>
              </div>
              <div className="flex items-center gap-6">
                <a href="mailto:team@dogboardingbaltimore.com" className="flex items-center gap-2 hover:text-amber-500">
                  <Mail className="w-4 h-4" />
                  team@dogboardingbaltimore.com
                </a>
              </div>
            </div>
            <div className="border-t border-stone-800 mt-8 pt-8 text-center text-stone-500 text-sm">
              © {new Date().getFullYear()} Dog Boarding Baltimore. Proudly serving {location.name} and all of Baltimore.
            </div>
          </div>
        </footer>

        <LeadForm isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} />
      </div>
    </>
  );
};

export default LocationPage;
